import './leasing-approved-item.scss';

import { useMemo, useState } from 'react';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { Button, OfferCard } from '@/components/Shared/ui';
import { SYMBOLS } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { formatPrice } from '@/services/formatter';
import { useAppSelector } from '@/store';
import type { TAutoCredLeasingResponse } from '@/types/newOrder';

import { getLeasingOfferBodyProducts } from '../../helpers/offerBodyProduct';
import useLeasingHeaderActions from '../../hooks/useLeasingHeaderActions';
import { LeasingDetails } from '../../ui';

type TLeasingApprovedItemsProps = {
  leasingOffer: TAutoCredLeasingResponse;
  hasOfferedBankResponse: boolean;
};

const LeasingApprovedItem = ({
  leasingOffer,
  hasOfferedBankResponse
}: TLeasingApprovedItemsProps) => {
  const [windowWidth] = useWindowSize();
  const mobileAlternatives = windowWidth <= 860;

  const { leasingOffers } = useAppSelector((state) => state.offerStep);
  const { leasingOrder } = useAppSelector((state) => state.newOrder);

  const [cancelModal, setCancelModal] = useState(false);
  const [isLeasingDetailsModalOpen, setIsLeasingDetailsModalOpen] =
    useState(false);

  const { headerActions } = useLeasingHeaderActions({
    leasingOffer
  });

  const additionalProducts = useMemo(() => {
    const products: React.ComponentProps<
      typeof OfferCard.Body
    >['additionalProducts'] = {
      title: 'Доп. продукты',
      content: []
    };

    if (leasingOffer.insuranceAmount) {
      products.content.push(
        `КАСКО ${SYMBOLS.longDash} ${formatPrice(leasingOffer.insuranceAmount)} ${SYMBOLS.tenge}`
      );
    }
    if (leasingOffer.telematicsAmount) {
      products.content.push(
        `Телематика ${SYMBOLS.longDash} ${formatPrice(leasingOffer.telematicsAmount)} ${SYMBOLS.tenge}`
      );
    }

    return products;
  }, [leasingOffer]);

  const isLeasingFinanced = useMemo(
    () =>
      leasingOffers.some(
        (offer) =>
          offer.status.code === 'financed' ||
          offer.status.code === 'waiting_of_financing'
      ),
    [leasingOffers]
  );

  const isSelected = useMemo(
    () =>
      leasingOffer.status.code === 'financed' ||
      leasingOffer.status.code === 'waiting_of_financing',
    [leasingOffer.status.code]
  );

  const headerTitle = leasingOffer.leasingCompany.name;
  const statusText = leasingOffer.status.name;

  const status =
    leasingOffer.status.code === 'cancelled' ||
    leasingOffer.status.code === 'declined' ||
    leasingOffer.status.code === 'error'
      ? 'disabled'
      : leasingOffer.status.code === 'waiting_bank_response' ||
          leasingOffer.status.code === 'waiting_of_financing'
        ? 'processing'
        : 'success';

  return (
    <>
      <OfferCard
        className="bank-item"
        status={status}
        isSelected={isSelected}
        statusText={statusText}
        isLeasing
      >
        <OfferCard.Header
          title={headerTitle}
          icon={leasingOffer.leasingCompany.icon}
          headerActions={headerActions}
          info={
            (isLeasingFinanced && !isSelected) || hasOfferedBankResponse
              ? 'Есть принятое предложение'
              : undefined
          }
          descriptionAction={
            leasingOffer.comment
              ? {
                  onClick: () => setIsLeasingDetailsModalOpen(true),
                  text: <span className="offer-body__hidden">Описание</span>
                }
              : undefined
          }
        />

        <OfferCard.Body
          footerActions={headerActions}
          products={getLeasingOfferBodyProducts({
            leasingOffer,
            leasingOrder,
            mobileAlternatives
          })}
          additionalProducts={additionalProducts}
        />
      </OfferCard>

      <CustomModal
        editClass="cancel-offer-modal"
        isModalOpen={cancelModal}
        handleOk={() => {
          setCancelModal(false);
        }}
        handleCancel={() => setCancelModal(false)}
        title="Вы уверены что хотите отклонить предложение данной лизинговой компании?"
        footer={[
          <Button key="back" onClick={() => setCancelModal(false)}>
            Нет
          </Button>,
          <Button
            key="submit"
            variant="filled-red"
            onClick={() => {
              setCancelModal(false);
            }}
          >
            Да, отменить
          </Button>
        ]}
      />

      <LeasingDetails
        isModalOpen={isLeasingDetailsModalOpen}
        onClose={() => setIsLeasingDetailsModalOpen(false)}
        leasingOffer={leasingOffer}
      />
    </>
  );
};

export default LeasingApprovedItem;
